import React, { useEffect, useRef, useState } from "react";
import { t } from "../../lib/i18n";

// The turn timer around a player's avatar (lib/turnClock.js), owner's request: a ring that empties
// clockwise, like a hand sweeping the time away — gold, orange from half the time (the last 15 s at
// most), red and beating for the last quarter (5 s at most), grey for a player away (short turns). The browser draws the drain (Web Animations), not React
// frame by frame: a loop of style changes would also weigh on PostHog's session recording. Time given
// back to the turn (a card placed with little left) refills it, with a « +9 s » rising from it.
const STROKE = { calm: "#fbbf24", late: "#fb923c", end: "#f43f5e", away: "#94a3b8" };

export function TurnRing({ clock, size, gap = 2, stroke = 2.5 }) {
  const arcRef = useRef(null);
  const prev = useRef(null);
  const [stage, setStage] = useState("calm");
  const [bonus, setBonus] = useState(null); // {key, s}: the last time given back
  const box = size + 2 * (gap + stroke);
  const r = (box - stroke) / 2;
  const C = 2 * Math.PI * r;

  useEffect(() => {
    const arc = arcRef.current;
    if (!clock || !arc) return undefined;
    const left = clock.deadline - performance.now();
    const frac = Math.min(1, Math.max(0, left / (clock.length * 1000)));
    const p = prev.current;
    // Within one clock, useTurnClock only moves the deadline later for time given back.
    if (p && p.id === clock.id && clock.deadline > p.deadline + 1000) {
      setBonus({ key: clock.deadline, s: Math.round((clock.deadline - Math.max(p.deadline, performance.now())) / 1000) });
    }
    prev.current = clock;
    const anim = arc.animate?.(
      [{ strokeDashoffset: -C * (1 - frac) }, { strokeDashoffset: -C }],
      { duration: Math.max(0, left), easing: "linear", fill: "forwards" },
    );
    // Orange from half the time (15 s at most), red for the last quarter (5 s at most): a short clock
    // starts gold too.
    const lateAt = Math.min(15000, clock.length * 500);
    const endAt = Math.min(5000, clock.length * 250);
    const update = () => {
      const l = clock.deadline - performance.now();
      setStage(clock.away ? "away" : l <= endAt ? "end" : l <= lateAt ? "late" : "calm");
    };
    update();
    const timers = [lateAt, endAt].map((s) => left - s).filter((d) => d > 0).map((d) => setTimeout(update, d + 20));
    return () => {
      anim?.cancel();
      timers.forEach(clearTimeout);
    };
  }, [clock, C]);

  if (!clock) return null;
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute left-1/2 top-1/2 z-10 ${stage === "end" ? "turn-ring-beat" : ""}`}
      style={{ width: box, height: box, marginLeft: -box / 2, marginTop: -box / 2 }}
      data-testid="turn-ring"
      data-stage={stage}
    >
      <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={box / 2} cy={box / 2} r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth={stroke} />
        <circle
          ref={arcRef}
          cx={box / 2}
          cy={box / 2}
          r={r}
          fill="none"
          stroke={STROKE[stage]}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={C}
          style={{ transition: "stroke 0.4s", filter: stage === "end" ? "drop-shadow(0 0 3px rgba(244,63,94,0.8))" : undefined }}
        />
      </svg>
      {bonus && bonus.s > 0 && (
        <span key={bonus.key} className="turn-ring-bonus absolute left-1/2 -top-3 text-[10px] text-emerald-300 tabular-nums whitespace-nowrap">
          {t("game.timer.bonus", { n: bonus.s })}
        </span>
      )}
    </span>
  );
}

export default TurnRing;
